package com.eladfinish.contract.email

import kotlinx.serialization.Serializable

/**
 * Request to send an email with a PDF attachment.
 *
 * Used by the "lexicographical sort" demo flow: the app generates a small PDF locally
 * and asks the server to email it to the user via Brevo.
 */
@Serializable
data class SendPdfEmailRequest(

    /**
     * Recipient's first name.
     */
    val firstName: String,

    /**
     * Recipient's last name.
     */
    val lastName: String,

    /**
     * Recipient's email address.
     */
    val email: String,

    /**
     * Base64-encoded PDF file content.
     */
    val pdfBase64: String,

    /**
     * Email subject line.
     */
    val subject: String,

    /**
     * Email body (HTML).
     */
    val htmlContent: String,

    /**
     * File name for the PDF attachment, including the ".pdf" extension.
     */
    val fileName: String = "demo.pdf",
)

@Serializable
data class SendPdfEmailResponse(
    val success: Boolean,
    val errorMessage: String? = null,
)

/**
 * Request to generate a PDF from HTML on the server and email it.
 * The server handles HTML→PDF conversion, so the app never needs to generate large PDFs locally.
 */
@Serializable
data class SendHtmlEmailRequest(
    val firstName: String,
    val lastName: String,
    val email: String,
    val subject: String,
    val emailBodyHtml: String,
    val tableHtml: String,
    val fileName: String = "lexicographical-table.pdf",
    val mikudChapterNumbers: List<Int>? = null,
    /** Pad the PDF with blank pages so the total page count is a multiple of 4. */
    val padPagesToMultipleOfFour: Boolean = false,
    /** Demo mode: keep only the first and last pages, with a teaser page in between. */
    val demo: Boolean = false,
    /** Recipient's Hebrew grammatical gender ("male"/"female") for personalized wording. */
    val hebrewGender: String = "male",
)

@Serializable
data class SendHtmlEmailResponse(
    val success: Boolean,
    val errorMessage: String? = null,
)
